import React, { Component, Fragment } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import api from '../../api';
import auth from '../../auth';
import Menues from '../../components/Menues';
import { childrenOf, visibleItems, isGroup } from '../../components/Sidebar/menuUtils';

import './Botonera.css';

/**
 * Arma el catálogo de botones a partir del menú: una entrada por sección, con
 * todas sus subsecciones visibles (links o modales), aplanando el tercer nivel.
 */
const buildCatalog = () => {
    const leaves = (items, section, parent) => visibleItems(items).reduce((all, item) => {
        if (isGroup(item)) return all.concat(leaves(childrenOf(item), section, item));
        if (!item.url && !item.component) return all;
        return all.concat({
            id: item.url || `modal:${section.name}/${parent ? parent.name + '/' : ''}${item.name}`,
            name: item.name,
            hint: parent ? parent.name : null,
            icon: item.icon || (parent && parent.icon) || section.icon,
            item,
        });
    }, []);

    return visibleItems(Menues.items)
        .map(section => ({
            name: section.name,
            icon: section.icon,
            buttons: isGroup(section) ? leaves(childrenOf(section), section) : leaves([section], section),
        }))
        .filter(section => section.buttons.length);
};

// Cuántos links de cada sección se ven antes de "Ver más"
const MODULOS_POR_SECCION = 5;
// Si el usuario dejó abierta la lista de todos los módulos (solo en este navegador)
const CLAVE_VER_MODULOS = 'dashboard_ver_modulos';

/*
    La botonera de cada usuario se guarda en este navegador: el backend no tiene
    dónde guardarla. null = el usuario nunca la configuró, se usa la de por defecto.
*/
const claveBotonera = (usuario) => 'dashboard_botonera' + (usuario ? '_' + usuario : '');

const leerBotonera = (usuario) => {
    try {
        const selected = JSON.parse(localStorage.getItem(claveBotonera(usuario)));
        return Array.isArray(selected) ? selected : null;
    } catch (e) {
        return null;
    }
};

const leerVerModulos = () => {
    try {
        return localStorage.getItem(CLAVE_VER_MODULOS) === '1';
    } catch (e) {
        return false;
    }
};

/** Botonera por defecto: la primera subsección de cada sección. */
const defaultSelection = (catalog) => catalog.map(section => section.buttons[0].id);

/**
 * Accesos rápidos del dashboard: los favoritos como tarjetas grandes y, debajo,
 * todos los módulos del menú como links. Cada usuario elige y ordena sus favoritos.
 */
class Botonera extends Component {
    constructor(props) {
        super(props);
        this.catalog = buildCatalog();
        this.state = {
            usuario: null,
            selected: null,
            loaded: false,
            editing: false,
            error: false,
            seccionesAbiertas: {},
            verModulos: leerVerModulos(),
            // Arrastrar y soltar (ver empezarArrastre)
            arrastre: null,
            destino: null,
        };
    }

    componentDidMount() {
        // El usuario solo se usa para separar la botonera de cada uno en el navegador
        axios.get(api.usuarios.usuarios.perfil, auth.header())
            .then(response => response.data.username)
            .catch(() => null)
            .then(usuario => this.setState({ usuario, selected: leerBotonera(usuario), loaded: true }));
    }

    getSelected = () => {
        const selected = this.state.selected || defaultSelection(this.catalog);
        // Se respeta el orden que eligió el usuario (los ordena arrastrándolos)
        const existentes = this.catalog.reduce((all, section) => all.concat(section.buttons.map(button => button.id)), []);
        return selected.filter(id => existentes.includes(id));
    }

    save = (selected) => {
        const clave = claveBotonera(this.state.usuario);
        try {
            if (selected) {
                localStorage.setItem(clave, JSON.stringify(selected));
            } else {
                localStorage.removeItem(clave);
            }
            this.setState({ selected, error: false });
        } catch (e) {
            this.setState({ error: true });
        }
    }

    toggle = (id) => {
        const selected = this.getSelected();
        this.save(selected.includes(id) ? selected.filter(x => x !== id) : selected.concat(id));
    }

    toggleModulos = () => {
        const verModulos = !this.state.verModulos;
        this.setState({ verModulos });
        try {
            localStorage.setItem(CLAVE_VER_MODULOS, verModulos ? '1' : '0');
        } catch (e) {
            // Sin almacenamiento solo no se recuerda
        }
    }

    reset = () => this.save(null);

    /*
        Arrastrar y soltar (configurando): los favoritos se reordenan arrastrándolos,
        un botón del catálogo se suma soltándolo entre los favoritos y un favorito
        se quita soltándolo sobre el catálogo.
        arrastre: { id, desde: 'favoritos' | 'catalogo' }
        destino:  { indice } entre los favoritos, o { quitar: true } sobre el catálogo
    */
    empezarArrastre = (e, id, desde) => {
        e.dataTransfer.effectAllowed = 'move';
        // Firefox no arrastra si no hay datos
        try { e.dataTransfer.setData('text/plain', id); } catch (err) { /* IE */ }
        this.setState({ arrastre: { id, desde }, destino: null });
    }

    terminarArrastre = () => this.setState({ arrastre: null, destino: null });

    setDestino = (destino) => {
        const actual = this.state.destino;
        if (JSON.stringify(actual) !== JSON.stringify(destino)) this.setState({ destino });
    }

    sobreFavorito = (e, indice) => {
        if (!this.state.arrastre) return;
        e.preventDefault();
        e.stopPropagation();
        // Antes o después según de qué mitad del botón esté el puntero
        const caja = e.currentTarget.getBoundingClientRect();
        this.setDestino({ indice: e.clientX > caja.left + caja.width / 2 ? indice + 1 : indice });
    }

    // El espacio libre de la grilla: va al final
    sobreFavoritos = (e) => {
        if (!this.state.arrastre) return;
        e.preventDefault();
        this.setDestino({ indice: this.getSelected().length });
    }

    salirDe = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) this.setDestino(null);
    }

    soltarEnFavoritos = (e) => {
        e.preventDefault();
        const { arrastre, destino } = this.state;
        this.terminarArrastre();
        if (!arrastre || !destino || destino.quitar) return;

        const selected = this.getSelected();
        const lista = selected.slice();
        let indice = destino.indice;
        const actual = lista.indexOf(arrastre.id);
        if (actual !== -1) {
            lista.splice(actual, 1);
            if (actual < indice) indice--;
        }
        lista.splice(indice, 0, arrastre.id);
        if (lista.join() !== selected.join()) this.save(lista);
    }

    sobreCatalogo = (e) => {
        if (!this.state.arrastre || this.state.arrastre.desde !== 'favoritos') return;
        e.preventDefault();
        this.setDestino({ quitar: true });
    }

    soltarEnCatalogo = (e) => {
        e.preventDefault();
        const { arrastre } = this.state;
        this.terminarArrastre();
        if (arrastre && arrastre.desde === 'favoritos') this.save(this.getSelected().filter(id => id !== arrastre.id));
    }

    // Props y clases de un favorito mientras se configura
    propsArrastre = (button, indice, total) => {
        const { arrastre, destino } = this.state;
        const clases = ['botonera-tile-wrapper--arrastrable'];
        if (arrastre && arrastre.id === button.id) clases.push('botonera-tile-wrapper--arrastrando');
        if (destino && !destino.quitar) {
            if (destino.indice === indice) clases.push('botonera-tile-wrapper--antes');
            if (destino.indice === total && indice === total - 1) clases.push('botonera-tile-wrapper--despues');
        }
        return {
            className: ' ' + clases.join(' '),
            draggable: true,
            onDragStart: e => this.empezarArrastre(e, button.id, 'favoritos'),
            onDragEnd: this.terminarArrastre,
            onDragOver: e => this.sobreFavorito(e, indice),
            onDrop: this.soltarEnFavoritos,
        };
    }

    renderTile = (button, arrastre) => {
        const { editing } = this.state;
        const { className: claseArrastre = '', ...propsArrastre } = (editing && arrastre) || {};
        // Sin subtítulo se centra el nombre al lado del ícono
        const claseTile = 'botonera-tile' + (button.hint ? '' : ' botonera-tile--sin-hint');
        const content = (
            <Fragment>
                <span className="botonera-tile__icon"><i className={button.icon}></i></span>
                <span className="botonera-tile__name">{button.name}</span>
                {button.hint && <span className="botonera-tile__hint">{button.hint}</span>}
            </Fragment>
        );

        return (
            <div className={'botonera-tile-wrapper' + claseArrastre} key={button.id} {...propsArrastre}>
                {button.item.component ?
                    <button.item.component nav={{ ...button.item, icon: null, name: content, className: claseTile }} /> :
                    <Link to={button.item.url} className={claseTile}>{content}</Link>
                }
                {editing &&
                    <button
                        type="button"
                        className="botonera-tile__remove"
                        title={`Quitar ${button.name}`}
                        onClick={() => this.toggle(button.id)}
                    >
                        <i className="fa fa-times"></i>
                    </button>
                }
            </div>
        );
    }

    renderCatalog = (selected) => {
        const quitando = !!(this.state.destino && this.state.destino.quitar);
        return (
            <div
                className={'botonera-catalog' + (quitando ? ' botonera-catalog--quitar' : '')}
                onDragOver={this.sobreCatalogo}
                onDragLeave={this.salirDe}
                onDrop={this.soltarEnCatalogo}
            >
                <h5 className="botonera-catalog__title">
                    <i className="fa fa-plus"></i>Catálogo de botones disponibles
                    <span className="botonera-catalog__limite">
                        {quitando ? 'Soltalo acá para quitarlo de los favoritos' : 'Hacé clic o arrastralos a los favoritos'}
                    </span>
                </h5>
                {this.catalog.map(section =>
                    <div className="botonera-catalog__section" key={section.name}>
                        <div className="botonera-section-title"><i className={section.icon}></i>{section.name}</div>
                        <div className="botonera-catalog__buttons">
                            {section.buttons.map(button => {
                                const active = selected.includes(button.id);
                                return (
                                    <button
                                        type="button"
                                        key={button.id}
                                        className={'botonera-chip' + (active ? ' active' : '') +
                                            (active ? '' : ' botonera-chip--arrastrable') +
                                            (this.state.arrastre && this.state.arrastre.id === button.id ? ' botonera-chip--arrastrando' : '')}
                                        draggable={!active}
                                        onDragStart={!active ? e => this.empezarArrastre(e, button.id, 'catalogo') : undefined}
                                        onDragEnd={!active ? this.terminarArrastre : undefined}
                                        onClick={() => this.toggle(button.id)}
                                    >
                                        {button.hint ? `${button.hint} · ${button.name}` : button.name}
                                        {active && <span className="botonera-chip__check"><i className="fa fa-check"></i></span>}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Las secciones largas se muestran recortadas hasta que se abren
    visibleModulos = (section) => this.state.seccionesAbiertas[section.name] ?
        section.buttons :
        section.buttons.slice(0, MODULOS_POR_SECCION);

    // Todas las secciones del menú como lista de links, debajo de los favoritos
    renderModulos = () => {
        const cantidad = this.catalog.reduce((total, section) => total + section.buttons.length, 0);
        const { verModulos } = this.state;
        return (
            <div className="botonera-modulos">
                <button
                    type="button"
                    className="botonera-modulos__encabezado"
                    aria-expanded={verModulos}
                    onClick={this.toggleModulos}
                >
                    <span><i className={'fa ' + (verModulos ? 'fa-chevron-down' : 'fa-chevron-right')}></i>Todos los módulos</span>
                    <span>{cantidad} accesos · {verModulos ? 'Ocultar' : 'Mostrar'}</span>
                </button>
                {verModulos && <div className="botonera-modulos__grilla">
                    {this.catalog.map(section =>
                        <div className="botonera-modulos__seccion" key={section.name}>
                            <div className="botonera-modulos__titulo"><i className={section.icon}></i>{section.name}</div>
                            {this.visibleModulos(section).map(button => {
                                const nombre = button.hint ? `${button.hint} · ${button.name}` : button.name;
                                return button.item.component ?
                                    <button.item.component key={button.id} nav={{ ...button.item, icon: null, name: nombre, className: 'botonera-modulos__link' }} /> :
                                    <Link key={button.id} to={button.item.url} className="botonera-modulos__link">{nombre}</Link>;
                            })}
                            {section.buttons.length > MODULOS_POR_SECCION &&
                                <button
                                    type="button"
                                    className="botonera-modulos__mas"
                                    onClick={() => this.setState(state => ({ seccionesAbiertas: { ...state.seccionesAbiertas, [section.name]: !state.seccionesAbiertas[section.name] } }))}
                                >
                                    {this.state.seccionesAbiertas[section.name] ? 'Ver menos' : `Ver ${section.buttons.length - MODULOS_POR_SECCION} más`}
                                </button>}
                        </div>
                    )}
                </div>}
            </div>
        );
    }

    render() {
        const { loaded, editing, error } = this.state;
        if (!loaded) return null;

        const selected = this.getSelected();
        const count = selected.length;

        const actions = (
            <div className="botonera-header__actions">
                {error && <span className="text-danger mr-3">No se pudo guardar la botonera</span>}
                {editing ?
                    <Fragment>
                        <span className="botonera-header__count">{`${count} ${count === 1 ? 'botón' : 'botones'}`}</span>
                        <button type="button" className="btn btn-link botonera-header__reset" onClick={this.reset}>
                            <i className="fa fa-undo"></i> Restablecer
                        </button>
                        <button type="button" className="btn btn-primary" onClick={() => this.setState({ editing: false })}>
                            <i className="fa fa-check"></i> Finalizar configuración
                        </button>
                    </Fragment> :
                    <button type="button" className="btn btn-outline-secondary bg-white" onClick={() => this.setState({ editing: true })}>
                        <i className="fa fa-cog"></i> Configurar botonera
                    </button>
                }
            </div>
        );

        // En el orden elegido. Sin tarjetas por sección, el nombre de la sección va abajo
        // del botón ("Certificación" hay en varias)
        const porId = {};
        this.catalog.forEach(section => section.buttons.forEach(button => {
            porId[button.id] = { ...button, hint: button.hint || (button.name !== section.name ? section.name : null) };
        }));
        const favoritos = selected.map(id => porId[id]);
        const soltarAca = editing ? {
            onDragOver: this.sobreFavoritos,
            onDragLeave: this.salirDe,
            onDrop: this.soltarEnFavoritos,
        } : {};
        return (
            <div className={'panel-tarjeta botonera-panel' + (editing ? ' botonera--editing' : '')}>
                <div className="panel-tarjeta__encabezado">
                    <div>
                        <h5 className="panel-tarjeta__titulo">Accesos rápidos</h5>
                        <span className="panel-tarjeta__subtitulo">
                            {editing ? 'Arrastrá los botones para cambiarlos de lugar' : 'Tus favoritos'}
                        </span>
                    </div>
                    {actions}
                </div>
                {favoritos.length ?
                    <div className="botonera-panel__favoritos" {...soltarAca}>
                        {favoritos.map((button, indice) => this.renderTile(button, this.propsArrastre(button, indice, favoritos.length)))}
                    </div> :
                    <div className="botonera-empty" {...soltarAca}>
                        No hay accesos favoritos. {editing ?
                            <span>Arrastrá acá botones del catálogo o hacé clic en ellos.</span> :
                            <span>Usá <b>Configurar botonera</b> para agregarlos.</span>}
                    </div>
                }
                {editing ? this.renderCatalog(selected) : this.renderModulos()}
            </div>
        );
    }
}

export default Botonera;



// WEBPACK FOOTER //
// ./src/views/Dashboard/Botonera.js